import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { baseUrl } from '../../../environments/environment';

export interface IPagination {
  page?: number;
  limit?: number;
  fetchAll?: boolean;
  sortBy?: string;
  sortOrder?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private http: HttpClient) {}

  /**
   * Get the base API URL
   */
  getBaseUrl(): string {
    return baseUrl;
  }

  /**
   * Process HTTP parameters, handling arrays properly
   * @param options The options object that may contain params
   * @returns A new options object with processed HttpParams
   */
  private processParams(options: any = {}): any {
    if (!options.params) {
      return options;
    }

    let httpParams = new HttpParams();
    const params = options.params;

    // Convert params object to HttpParams, handling arrays
    Object.keys(params).forEach((key) => {
      if (params[key] !== null && params[key] !== undefined) {
        // Handle arrays by appending each value with the same key
        if (Array.isArray(params[key])) {
          params[key].forEach((value: any) => {
            if (value !== null && value !== undefined) {
              httpParams = httpParams.append(key, value.toString());
            }
          });
        } else {
          httpParams = httpParams.set(key, params[key].toString());
        }
      }
    });

    // Return new options object with the processed HttpParams
    return {
      ...options,
      params: httpParams,
    };
  }

  get<T = any>(url: string, options: any = {}): Observable<T> {
    const processedOptions = this.processParams(options);
    return this.http.get(baseUrl + url, processedOptions) as Observable<T>;
  }

  post<T = any>(url: string, body: any, options: any = {}): Observable<T> {
    const processedOptions = this.processParams(options);
    return this.http.post(
      baseUrl + url,
      body,
      processedOptions
    ) as Observable<T>;
  }

  put<T = any>(url: string, body: any, options: any = {}): Observable<T> {
    const processedOptions = this.processParams(options);
    return this.http.put(
      baseUrl + url,
      body,
      processedOptions
    ) as Observable<T>;
  }

  patch<T = any>(url: string, body: any, options: any = {}): Observable<T> {
    const processedOptions = this.processParams(options);
    return this.http.patch(
      baseUrl + url,
      body,
      processedOptions
    ) as Observable<T>;
  }

  delete<T = any>(url: string, options: any = {}): Observable<T> {
    const processedOptions = this.processParams(options);
    return this.http.delete(baseUrl + url, processedOptions) as Observable<T>;
  }
}
